'use client';

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@ui/button';
import { PillText } from '@web/atoms';
import { ResponsiveImage } from '@web/molecules';
import { useReporter } from '../../reporter';
import {
  Wrapper,
  Layout,
  MobileImageContainer,
  DesktopImageContainer,
  FormContainer,
  Heading,
  Subheading,
  ButtonStack,
  HeadlineWrapper,
  MultipetBanner,
  SecondaryButtonWrapper,
  SecondaryAction,
  LogoLink,
} from './styles.js';
import {
  userClicksQuizAbandonerMenuCTA,
  userViewsQuizAbandonerMenu,
} from './events';

export const QuizAbandonerMenu = ({
  headline,
  subheadline,
  ollieLogo,
  mobileImage,
  desktopImage,
  imagePillText,
  imagePillVariant = 'success',
  primaryCtaText,
  onPrimaryCtaClick,
  isCtaLoading = false,
  secondaryCtaButtons,
  multipetBannerText,
  petCount,
  furthestStep,
  bucket,
}) => {
  const reporter = useReporter();

  useEffect(() => {
    reporter.tag(userViewsQuizAbandonerMenu(petCount, furthestStep, bucket));
  }, [reporter, petCount, furthestStep, bucket]);

  return (
    <Wrapper>
      <Layout>
        <DesktopImageContainer>
          {desktopImage && (
            <ResponsiveImage priority={true} {...desktopImage} />
          )}
        </DesktopImageContainer>

        <FormContainer>
          {ollieLogo && (
            <LogoLink href={'/'}>
              <ResponsiveImage {...ollieLogo} alt="Ollie logo" />
            </LogoLink>
          )}

          {imagePillText && (
            <PillText text={imagePillText} variant={imagePillVariant} />
          )}

          {mobileImage && (
            <MobileImageContainer>
              <ResponsiveImage priority={true} {...mobileImage} />
            </MobileImageContainer>
          )}

          {multipetBannerText && (
            <MultipetBanner>{multipetBannerText}</MultipetBanner>
          )}

          <HeadlineWrapper>
            <Heading>{headline}</Heading>
            <Subheading>{subheadline}</Subheading>
          </HeadlineWrapper>

          <ButtonStack>
            <Button
              aria-label={primaryCtaText}
              onClick={() => {
                reporter.tag(
                  userClicksQuizAbandonerMenuCTA(primaryCtaText, 'primary'),
                );
                onPrimaryCtaClick();
              }}
              isLoading={isCtaLoading}
              disabled={isCtaLoading}
              fullWidth
              variant="primary"
            >
              {primaryCtaText}
            </Button>
            <SecondaryButtonWrapper>
              {secondaryCtaButtons?.map((ctaButton, i) => (
                <SecondaryAction
                  key={i}
                  type="button"
                  aria-label={ctaButton.secondaryCtaText}
                  onClick={() => {
                    reporter.tag(
                      userClicksQuizAbandonerMenuCTA(
                        ctaButton.secondaryCtaText,
                        'secondary',
                      ),
                    );
                    ctaButton.onSecondaryCtaClick();
                  }}
                  disabled={isCtaLoading}
                >
                  {ctaButton.secondaryCtaText}
                </SecondaryAction>
              ))}
            </SecondaryButtonWrapper>
          </ButtonStack>
        </FormContainer>
      </Layout>
    </Wrapper>
  );
};

QuizAbandonerMenu.displayName = 'QuizAbandonerMenu';

QuizAbandonerMenu.propTypes = {
  headline: PropTypes.string.isRequired,
  subheadline: PropTypes.string.isRequired,
  ollieLogo: PropTypes.object,
  mobileImage: PropTypes.object,
  desktopImage: PropTypes.object,
  imagePillText: PropTypes.string,
  imagePillVariant: PropTypes.string,
  primaryCtaText: PropTypes.string.isRequired,
  onPrimaryCtaClick: PropTypes.func.isRequired,
  isCtaLoading: PropTypes.bool,
  secondaryCtaButtons: PropTypes.arrayOf(
    PropTypes.shape({
      secondaryCtaText: PropTypes.string.isRequired,
      onSecondaryCtaClick: PropTypes.func.isRequired,
    }),
  ),
  multipetBannerText: PropTypes.string,
  petCount: PropTypes.number,
  furthestStep: PropTypes.string,
  bucket: PropTypes.string,
};
